"use client";

import type { ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  addDays,
  addMonths,
  addWeeks,
  isSameMonth,
  monthGrid,
  monthLabel,
  weekDays,
  weekStartFor,
  type BookingView,
} from "@/modules/booking/surfaces";
import {
  formatTimeOfDay,
  weekdayOf,
  type TimeBlock,
} from "@/modules/scheduling/surfaces";
import { bookingStatus, type StatusTone } from "./bookingStatus";
import { serviceLocationLabel } from "@/modules/shared/serviceLocation";
import { DataUnavailable } from "./DataUnavailable";

/**
 * BusinessCalendarScreen (docs/ux/screens/BusinessCalendarScreen.md), shared by
 * both frontends (R22 T14).
 *
 * The clinic's week, month or day, as opposed to the list the queue screen
 * shows. **Read-only**: nothing is decided here, which is why the whole of it
 * moves with only a `Link` injected. Deciding is the queue's job, and the
 * "List" control at the top is the way back to it.
 *
 * **Everything that says what is on screen lives in the URL** (`?view=&date=
 * &practitioner=`), so every control is a plain link, the browser's back button
 * works through a month of navigation, and a colleague can be sent "look at
 * Thursday".
 *
 * The day grid is the one view that needs the **derived calendar** (H40): the
 * clinic's opening hours minus each person's Blackouts, which is why it is
 * asked for by date rather than by weekday.
 */
/** Which of the three shapes the clinic is looking at. */
export type View = "day" | "week" | "month";

export type CalendarLink = (props: {
  href: string;
  className?: string;
  "aria-current"?: "page" | "true" | undefined;
  children: ReactNode;
}) => ReactNode;

export function parseView(raw: string | string[] | undefined | null): View {
  return raw === "day" || raw === "month" ? raw : "week";
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function parseDate(
  raw: string | string[] | undefined | null,
  fallback: string,
): string {
  if (typeof raw !== "string" || !ISO_DATE.test(raw)) return fallback;
  // A syntactically valid but impossible date ("2026-02-31") would silently
  // roll over, so reject anything the Date constructor rewrites.
  return new Date(`${raw}T12:00:00Z`).toISOString().slice(0, 10) === raw
    ? raw
    : fallback;
}

/** The inclusive span a view needs, so one query serves all three. */
export function spanFor(view: View, date: string): { from: string; to: string } {
  if (view === "day") return { from: date, to: date };
  if (view === "week") {
    const start = weekStartFor(date);
    return { from: start, to: addDays(start, 6) };
  }
  const cells = monthGrid(date);
  return { from: cells[0], to: cells[cells.length - 1] };
}

export function BusinessCalendar({
  view,
  cursor,
  today,
  practitioners,
  practitionerFilter,
  bookings,
  patterns,
  configured = true,
  Link,
}: {
  view: View;
  /** The day the view is centred on. */
  cursor: string;
  today: string;
  practitioners: { id: string; displayName: string }[];
  /** "all", or one practitioner's id. */
  practitionerFilter: string;
  /** Null when the read failed. */
  bookings: BookingView[] | null;
  /**
   * The derived open blocks per practitioner for the shown day (H40). A plain
   * object rather than a Map, because it crosses the wire: the server composes
   * it and a JSON body has no Maps.
   */
  patterns: Record<string, TimeBlock[]>;
  configured?: boolean;
  Link: CalendarLink;
}) {
  const link = (next: { view?: View; date?: string; practitioner?: string }) => {
    const sp = new URLSearchParams({
      view: next.view ?? view,
      date: next.date ?? cursor,
    });
    const who = next.practitioner ?? practitionerFilter;
    if (who !== "all") sp.set("practitioner", who);
    return `/business/calendar?${sp.toString()}`;
  };

  const stepBack =
    view === "month"
      ? addMonths(cursor, -1)
      : view === "week"
        ? addWeeks(cursor, -1)
        : addDays(cursor, -1);
  const stepOn =
    view === "month"
      ? addMonths(cursor, 1)
      : view === "week"
        ? addWeeks(cursor, 1)
        : addDays(cursor, 1);

  return (
    <>
        <Link href="/business" className="mb-4 inline-block text-sm font-medium text-accent">
          ← Back to your business
        </Link>

        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-xl font-bold text-ink">Schedule</h1>
          <ListLink Link={Link} />
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-line bg-surface p-0.5 text-sm">
            {(["day", "week", "month"] as View[]).map((option) => (
              <Link
                key={option}
                href={link({ view: option })}
                aria-current={option === view ? "true" : undefined}
                className={`rounded-md px-3 py-1.5 font-semibold capitalize ${
                  option === view
                    ? "bg-primary text-on-primary"
                    : "text-muted hover:text-ink"
                }`}
              >
                {option}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-1">
            <Step href={link({ date: stepBack })} label="Previous" Link={Link}>
              <ChevronLeft className="size-4" aria-hidden />
            </Step>
            <Link
              href={link({ date: today })}
              className="rounded-lg border border-line bg-surface px-3 py-2 text-sm font-semibold text-body hover:border-accent/50 hover:text-ink"
            >
              Today
            </Link>
            <Step href={link({ date: stepOn })} label="Next" Link={Link}>
              <ChevronRight className="size-4" aria-hidden />
            </Step>
          </div>

          <p className="font-semibold text-ink">{rangeLabel(view, cursor)}</p>
        </div>

        {practitioners.length > 1 && (
          <PractitionerFilter
            practitioners={practitioners}
            active={practitionerFilter}
            link={link}
            Link={Link}
          />
        )}

        {bookings === null ? (
          <DataUnavailable configured={configured} what="your calendar" />
        ) : view === "day" ? (
          <DayView
            date={cursor}
            today={today}
            bookings={bookings}
            practitioners={practitioners.filter(
              (p) => practitionerFilter === "all" || p.id === practitionerFilter,
            )}
            patterns={patterns}
          />
        ) : view === "week" ? (
          <WeekView
            weekStart={weekStartFor(cursor)}
            today={today}
            bookings={bookings}
            link={link}
            Link={Link}
          />
        ) : (
          <MonthView
            cursor={cursor}
            today={today}
            bookings={bookings}
            link={link}
            Link={Link}
          />
        )}
    </>
  );
}

/* ------------------------------------------------------------------ day -- */

/**
 * The working day as a grid: one column per practitioner, one row per half
 * hour of the hours they actually work. Shading the hours nobody is working is
 * what makes a gap legible as a gap rather than as an absence of information.
 */
function DayView({
  date,
  today,
  bookings,
  practitioners,
  patterns,
}: {
  date: string;
  today: string;
  bookings: BookingView[];
  practitioners: { id: string; displayName: string }[];
  /**
   * The derived open blocks per practitioner (H40), keyed by id. A plain object
   * rather than a Map because it crosses the wire from the API.
   */
  patterns: Record<string, TimeBlock[]>;
}) {
  const weekday = weekdayOf(date);
  const working = practitioners.filter((p) =>
    (patterns[p.id] ?? []).some((b) => b.weekday === weekday),
  );

  if (working.length === 0) {
    return (
      <Empty
        title={`Nobody is working on ${dayLabel(date, true)}`}
        body="Set weekly availability from a practitioner's page to open this day."
      />
    );
  }

  // The grid runs from the earliest start to the latest end anyone works, so a
  // clinic that opens at 07:00 on Saturdays is not padded with dead rows.
  const blocks = working.flatMap((p) =>
    (patterns[p.id] ?? []).filter((b) => b.weekday === weekday),
  );
  const from = Math.min(...blocks.map((b) => b.startMinutes));
  const to = Math.max(...blocks.map((b) => b.endMinutes));
  const STEP = 30;
  const rows: number[] = [];
  for (let m = Math.floor(from / STEP) * STEP; m < to; m += STEP) rows.push(m);

  const worksAt = (practitionerId: string, minute: number) =>
    (patterns[practitionerId] ?? []).some(
      (b) => b.weekday === weekday && minute >= b.startMinutes && minute < b.endMinutes,
    );

  // Every row a booking covers, so a 90 minute service fills three of them and
  // only the first carries the label.
  const covering = new Map<string, BookingView>();
  for (const booking of bookings.filter((b) => b.blockDate === date)) {
    const key = booking.practitionerId ?? "unassigned";
    for (let m = booking.startMinutes; m < booking.endMinutes; m += STEP) {
      covering.set(`${key}|${Math.floor(m / STEP) * STEP}`, booking);
    }
  }

  const columns = `4rem repeat(${working.length}, minmax(0, 1fr))`;
  const unassigned = bookings.filter(
    (b) => b.blockDate === date && b.practitionerId === null,
  );

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
        <div className="grid border-b border-line bg-page" style={{ gridTemplateColumns: columns }}>
          <span />
          {working.map((p) => (
            <span key={p.id} className="min-w-0 truncate px-2 py-2.5 text-center text-xs font-bold text-ink">
              {p.displayName}
            </span>
          ))}
        </div>

        {rows.map((minute, i) => (
          <div
            key={minute}
            className={`grid ${i > 0 ? "border-t border-line" : ""}`}
            style={{ gridTemplateColumns: columns }}
          >
            <span className="flex items-center justify-end border-e border-line py-2 pe-2 font-mono text-[11px] text-muted">
              {formatTimeOfDay(minute)}
            </span>
            {working.map((p) => {
              const booking = covering.get(`${p.id}|${minute}`);
              const starts = booking?.startMinutes === minute;
              if (!worksAt(p.id, minute)) {
                return (
                  <span
                    key={p.id}
                    className="border-s border-line bg-page/60"
                    aria-label={`${p.displayName} is not working at ${formatTimeOfDay(minute)}`}
                  />
                );
              }
              if (!booking) {
                return <span key={p.id} className="min-h-9 border-s border-line" />;
              }
              return (
                <span
                  key={p.id}
                  className={`min-w-0 border-s border-line bg-accent/10 px-2 ${starts ? "py-1.5" : ""}`}
                >
                  {starts && (
                    <>
                      <span className="block truncate text-xs font-semibold text-accent">
                        {booking.consumerName}
                      </span>
                      <span className="block truncate text-[11px] text-accent/80">
                        {booking.serviceName}
                      </span>
                    </>
                  )}
                </span>
              );
            })}
          </div>
        ))}
      </div>

      <p className="mt-3 text-xs text-muted">
        Shaded means that practitioner is not working then.
        {date === today ? " Today." : ""}
      </p>

      {/* An approved booking always has a practitioner (H23 assigns at
          approval), so anything here is still awaiting a decision. It has no
          column to sit in, and dropping it would hide work. */}
      {unassigned.length > 0 && (
        <div className="mt-4 rounded-xl border border-line bg-surface p-4 shadow-sm">
          <h2 className="text-sm font-bold text-ink">Not yet assigned</h2>
          <ul className="mt-2 space-y-2">
            {unassigned.map((booking) => (
              <li key={booking.id} className="text-sm">
                <span className="font-mono text-muted">
                  {formatTimeOfDay(booking.startMinutes)}
                </span>{" "}
                <span className="font-medium text-ink">{booking.consumerName}</span>{" "}
                <span className="text-muted">{booking.serviceName}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

/* ----------------------------------------------------------------- week -- */

function WeekView({
  weekStart,
  today,
  bookings,
  link,
  Link,
}: {
  weekStart: string;
  today: string;
  bookings: BookingView[];
  link: (next: { view?: View; date?: string }) => string;
  Link: CalendarLink;
}) {
  const days = weekDays(weekStart);
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7">
      {days.map((date) => {
        const items = bookings.filter((b) => b.blockDate === date);
        const isToday = date === today;
        return (
          <Link
            key={date}
            href={link({ view: "day", date })}
            className={`flex flex-col rounded-xl border p-3 shadow-sm transition-colors hover:border-accent/50 sm:min-h-[120px] ${
              isToday ? "border-accent bg-accent/5" : "border-line bg-surface"
            }`}
          >
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
              {dayLabel(date, false)}
              {isToday && <span className="ms-1 text-accent">• today</span>}
            </p>
            {items.length === 0 ? (
              <p className="py-2 text-center text-xs text-muted/70">No bookings</p>
            ) : (
              <ul className="space-y-2">
                {items.map((booking) => (
                  <BookingBlock key={booking.id} booking={booking} />
                ))}
              </ul>
            )}
          </Link>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------- month -- */

/** A density grid, for finding the quiet fortnight rather than reading a day. */
function MonthView({
  cursor,
  today,
  bookings,
  link,
  Link,
}: {
  cursor: string;
  today: string;
  bookings: BookingView[];
  link: (next: { view?: View; date?: string }) => string;
  Link: CalendarLink;
}) {
  const cells = monthGrid(cursor);
  const counts = new Map<string, number>();
  for (const booking of bookings) {
    counts.set(booking.blockDate, (counts.get(booking.blockDate) ?? 0) + 1);
  }
  const busiest = Math.max(1, ...counts.values());

  return (
    <div className="overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
      <div className="grid grid-cols-7 border-b border-line bg-page">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <span key={d} className="px-1 py-2 text-center text-xs font-bold text-muted">
            {d}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((date) => {
          const count = counts.get(date) ?? 0;
          const outside = !isSameMonth(date, cursor);
          const isToday = date === today;
          return (
            <Link
              key={date}
              href={link({ view: "day", date })}
              className={`flex aspect-square flex-col items-center justify-center gap-1 border-b border-e border-line text-sm hover:bg-page ${
                outside ? "text-muted/40" : "text-ink"
              }`}
            >
              <span
                className={`flex size-7 items-center justify-center rounded-md font-mono font-semibold ${
                  isToday ? "bg-primary text-on-primary" : ""
                }`}
              >
                {Number(date.slice(8, 10))}
              </span>
              {count > 0 && (
                <span
                  className="h-1.5 rounded-[2px] bg-accent"
                  style={{ width: `${Math.max(20, (count / busiest) * 60)}%` }}
                  aria-label={`${count} ${count === 1 ? "booking" : "bookings"}`}
                />
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- bits -- */

function ListLink({ Link }: { Link: CalendarLink }) {
  return (
    <Link
      href="/business/bookings"
      className="rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-muted hover:border-accent/50 hover:text-ink"
    >
      List and requests
    </Link>
  );
}

function Step({
  href,
  label,
  children,
  Link,
}: {
  href: string;
  label: string;
  children: React.ReactNode;
  Link: CalendarLink;
}) {
  return (
    <Link
      href={href}
      aria-label={label}
      className="inline-flex size-9 items-center justify-center rounded-lg border border-line bg-surface text-body hover:border-accent/50 hover:text-ink"
    >
      {children}
    </Link>
  );
}

function PractitionerFilter({
  practitioners,
  active,
  link,
  Link,
}: {
  practitioners: { id: string; displayName: string }[];
  active: string;
  link: (next: { practitioner?: string }) => string;
  Link: CalendarLink;
}) {
  const chip = (isActive: boolean) =>
    `rounded-lg border px-3 py-1.5 text-sm font-medium ${
      isActive
        ? "border-accent bg-accent text-on-accent"
        : "border-line bg-surface text-body hover:border-accent/50"
    }`;
  return (
    <div className="mb-4 flex flex-wrap gap-2">
      <Link href={link({ practitioner: "all" })} className={chip(active === "all")}>
        Everyone
      </Link>
      {practitioners.map((p) => (
        <Link
          key={p.id}
          href={link({ practitioner: p.id })}
          className={chip(active === p.id)}
        >
          {p.displayName}
        </Link>
      ))}
    </div>
  );
}

function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-line bg-surface p-10 text-center shadow-sm">
      <p className="font-semibold text-ink">{title}</p>
      <p className="mt-1 text-sm text-muted">{body}</p>
    </div>
  );
}

/** Tone → start-border colour: a compact status cue that never needs a wide pill. */
const TONE_BORDER: Record<StatusTone, string> = {
  success: "border-s-success",
  danger: "border-s-danger",
  warning: "border-s-warning",
  info: "border-s-accent",
  muted: "border-s-line",
};

function BookingBlock({ booking }: { booking: BookingView }) {
  const tone = bookingStatus(booking.state).tone;
  return (
    <li
      className={`rounded-lg border border-line border-s-4 ${TONE_BORDER[tone]} bg-page p-2 text-xs`}
    >
      <div className="flex items-baseline justify-between gap-1">
        <span className="font-semibold text-ink">
          {formatTimeOfDay(booking.startMinutes)}
        </span>
        <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-muted">
          {shortStatus(booking.state)}
        </span>
      </div>
      <p className="mt-0.5 truncate font-medium text-ink">{booking.consumerName}</p>
      <p className="truncate text-muted">
        {booking.serviceName} ·{" "}
        {booking.practitionerName ?? <span className="italic">Any</span>}
      </p>
      <p className="truncate text-[11px] text-muted">
        {serviceLocationLabel(booking.serviceLocation)}
      </p>
    </li>
  );
}

/** One-word status for the calendar (the full pill is too wide per block). */
function shortStatus(state: BookingView["state"]): string {
  switch (state) {
    case "requested":
      return "Pending";
    case "approved":
      return "Confirmed";
    case "completed":
      return "Done";
    case "declined":
      return "Declined";
    case "expired":
      return "Expired";
    case "withdrawn":
      return "Withdrawn";
    case "cancelled_by_consumer":
    case "cancelled_by_business":
      return "Cancelled";
  }
}

function dayLabel(date: string, long: boolean): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString("en-GB", {
    weekday: long ? "long" : "short",
    day: "numeric",
    month: long ? "long" : undefined,
    timeZone: "UTC",
  });
}

function rangeLabel(view: View, cursor: string): string {
  if (view === "month") return monthLabel(cursor);
  if (view === "day") return dayLabel(cursor, true);
  const start = weekStartFor(cursor);
  return `${dayLabel(start, false)} to ${dayLabel(addDays(start, 6), false)}`;
}
